import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { Table, DatePicker, Select, Descriptions, Modal, Spin, Timeline, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { AuthContext } from "../../context/AuthContext";

const { Text } = Typography;

export default function AppliedOnDuty() {
    const { authData, backUrl } = useContext(AuthContext);

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const [filters, setFilters] = useState({
        status: null,
        month: null,
        year: null,
        fromDate: null,
        toDate: null,
    });

    const [showLead, setShowLead] = useState(false);
    const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const fetchData = async (filterValues = filters) => {
        try {
            setLoading(true);

            const params = {};

            if (filterValues.status) params.status = filterValues.status;
            if (filterValues.month) params.month = filterValues.month;
            if (filterValues.year) params.year = filterValues.year;
            if (filterValues.fromDate)
                params.from_date = dayjs(filterValues.fromDate).format("YYYY-MM-DD");
            if (filterValues.toDate)
                params.to_date = dayjs(filterValues.toDate).format("YYYY-MM-DD");

            const res = await axios.get(
                `${backUrl}/api/hr/attendance/my-onduty-requests/`,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                    params,
                }
            );

            setRequests(res.data.results || res.data);
        } catch (error) {
            console.error("Error fetching On Duty requests:", error.response || error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const updateFilter = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        fetchData(newFilters);
    };

    const statusColors = {
        PENDING: "#faad14",
        APPROVED: "green",
        REJECTED: "red",
    };

    const getStatusColor = (status) => {
        if (!status) return "#faad14";
        return statusColors[status.toUpperCase()] || "#faad14";
    };

    const columns = [
        {
            title: "Employee Name",
            dataIndex: "employee_name",
            key: "employee_name",
        },
        {
            title: "On Duty Type",
            dataIndex: "onduty_type",
            key: "onduty_type",
            render: (text, record) => (
                <>
                    <p>{text}</p>
                    <p>{record.check_in_out_mode}</p>

                    {(() => {
                        const raw = record?.lead_data || record?.lead_details || record?.external_lead_details;
                        if (!raw) return false;
                        try {
                            const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                            if (Array.isArray(parsed)) return parsed.length > 0;
                            if (typeof parsed === "object" && parsed !== null) return Object.keys(parsed).length > 0;
                            return !!parsed;
                        } catch (e) {
                            return typeof raw === "string" && raw.trim() !== "" && raw !== "[]" && raw !== "{}";
                        }
                    })() && (
                        <a
                            onClick={() => {
                                const leadData = record.lead_data || record.lead_details || record.external_lead_details;
                                let parsedLeads = leadData;
                                if (typeof leadData === "string") {
                                    try {
                                        parsedLeads = JSON.parse(leadData);
                                    } catch (e) {
                                        console.error("Failed to parse lead_data", e);
                                    }
                                }
                                const leadsArray = Array.isArray(parsedLeads) ? parsedLeads : [parsedLeads];
                                setSelectedLeadDetails(leadsArray);
                                setShowLead(true);
                            }}
                            style={{ color: "#1677ff", cursor: "pointer", display: "block", marginTop: "4px" }}
                        >
                            Lead Details
                        </a>
                    )}
                </>
            ),
        },
        {
            title: "Check-In Photo",
            dataIndex: "photo",
            key: "check_in_photo",
            render: (url) =>
                url ? (
                    <img
                        src={url}
                        alt="Check-In"
                        style={{
                            width: 50,
                            height: 50,
                            objectFit: "cover",
                            borderRadius: 4,
                        }}
                    />
                ) : (
                    "-"
                ),
        },
        {
            title: "Location",
            dataIndex: "location",
            key: "location",
            width: 250,
            render: (text, record) =>
                ((record.check_in_latitude && record.check_in_longitude) ||
                    (record.check_out_latitude && record.check_out_longitude)) ? (
                    <a
                        href={`https://www.google.com/maps?q=${record.check_in_latitude || record.check_out_latitude
                            },${record.check_in_longitude || record.check_out_longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1677ff" }}
                    >
                        {text || "View Location"}
                    </a>
                ) : (
                    text || "-"
                ),
        },
        {
            title: "Start Time",
            dataIndex: "start_time",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "End Time",
            dataIndex: "end_time",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
        {
            title: "Remarks",
            dataIndex: "remarks",
            width: 300,
            render: (text) => (
                <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                    {text || "-"}
                </div>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (text) => (
                <span style={{ fontWeight: 500, color: getStatusColor(text) }}>
                    {text
                        ? String(text).charAt(0).toUpperCase() +
                        String(text).slice(1).toLowerCase()
                        : "-"}
                </span>
            ),
        },
        {
            title: "Approval Hierarchy",
            key: "approval_logs",
            width: 400,
            render: (_, record) => {
                const logs = record.approval_logs || [];
                if (!logs.length) return "-";

                return (
                    <Timeline style={{ marginTop: "20px" }}>
                        {logs.map((log, index) => {
                            const statusColor =
                                log.status === "APPROVED"
                                    ? "green"
                                    : log.status === "REJECTED"
                                        ? "red"
                                        : "gray";

                            return (
                                <Timeline.Item key={index} color={statusColor}>
                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                            <div>
                                                <Text strong style={{ fontSize: "14px" }}>
                                                    {log.approver_name}
                                                </Text>
                                            </div>
                                            <Tag color={statusColor}>{log.status === "Cancelled" ? "Skipped (On-Duty Cancelled)" : log.status}</Tag>
                                        </div>

                                        {/* Combined Action Details (Additive) */}
                                        <div style={{ marginTop: "6px" }}>
                                            {/* Regular Approval / Rejection Block */}
                                            {(log.updated_at || log.remarks) && (
                                                <div style={{ marginBottom: "8px" }}>
                                                    {log.updated_at && (
                                                        <div style={{ color: "#595959", fontSize: "12px", marginBottom: "2px" }}>
                                                            <Text strong style={{ fontSize: "11px", color: "#595959" }}>{log.status === "REJECTED" ? "Rejected On: " : "On: "}</Text>
                                                            {dayjs(log.updated_at).format("DD MMM YYYY [at] hh:mm A")}
                                                        </div>
                                                    )}
                                                    {log.remarks && (
                                                        <div style={{ fontSize: "13px" }}>
                                                            <Text strong>Remark: </Text>
                                                            <Text>{log.remarks}</Text>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Cancellation Block */}
                                            {(log.cancellation_approved_on || log.cancellation_rejected_on || log.cancellation_reason) && (
                                                <div style={{
                                                    marginTop: (log.updated_at || log.remarks) ? "8px" : "0",
                                                    paddingTop: (log.updated_at || log.remarks) ? "8px" : "0",
                                                    borderTop: (log.updated_at || log.remarks) ? "1px dashed #e8e8e8" : "none"
                                                }}>
                                                    {(log.cancellation_approved_on || log.cancellation_rejected_on) && (
                                                        <div style={{ color: "#595959", fontSize: "12px", marginBottom: "2px" }}>
                                                            <Text strong style={{ fontSize: "11px", color: "#595959" }}>Cancellation {log.cancellation_rejected_on ? "Rejected On: " : "Approved On: "}</Text>
                                                            {dayjs(log.cancellation_approved_on || log.cancellation_rejected_on).format("DD MMM YYYY [at] hh:mm A")}
                                                        </div>
                                                    )}
                                                    {log.cancellation_reason && (
                                                        <div style={{ fontSize: "13px" }}>
                                                            <Text strong>Cancellation Remark: </Text>
                                                            <Text>{log.cancellation_reason}</Text>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Timeline.Item>
                            );
                        })}
                    </Timeline>
                );
            },
        },
    ];

    const years = Array.from(
        new Set(requests.map((item) => dayjs(item.start_time).year()))
    ).sort((a, b) => b - a);

    return (
        <>
            {/* Lead Modal */}

            <Modal
                open={showLead}
                footer={null}
                onCancel={() => {
                    setShowLead(false);
                    setSelectedLeadDetails(null);
                }}
                width={700}
            >
                {selectedLeadDetails && selectedLeadDetails.length > 0 ? (
                    <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                        {selectedLeadDetails.map((lead, index) => (
                            <Descriptions
                                key={index}
                                bordered
                                column={1}
                                size="small"
                                style={{ marginBottom: 16 }}
                                title={selectedLeadDetails.length > 1 ? `Lead ${index + 1}` : null}
                            >
                                <Descriptions.Item label="Lead Number">
                                    <strong>{lead?.lead_no || lead?.lead_id || "-"}</strong>
                                </Descriptions.Item>
                                <Descriptions.Item label="Customer Name">
                                    {lead?.customer_name || lead?.name || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Email">
                                    {lead?.email || lead?.customer_email || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Phone Number">
                                    {lead?.number || lead?.phone || lead?.customer_phone || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Project Name">
                                    {lead?.project_name || lead?.project_id || (lead?.project && lead.project.title) || "-"}
                                </Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <span
                                        style={{
                                            padding: "4px 12px",
                                            borderRadius: "4px",
                                            backgroundColor: "#f6ffed",
                                            color: String(lead?.status?.title || lead?.status || "").toLowerCase().includes("cancel")
                                                ? "#d46b08"
                                                : "#52c41a",
                                            fontWeight: 500,
                                            border: "1px solid #b7eb8f"
                                        }}
                                    >
                                        {lead?.status?.title || lead?.status || "-"}
                                    </span>
                                </Descriptions.Item>
                            </Descriptions>
                        ))}
                    </div>
                ) : (
                    <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                        No lead details available
                    </div>
                )}
            </Modal>

            {/* Filters */}

            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "10px",
                    marginBottom: 16,
                    flexDirection: isMobile ? "column" : "row",
                }}
            >
                <Select
                    placeholder="Year"
                    allowClear
                    style={{ width: 140 }}
                    onChange={(val) => updateFilter("year", val)}
                >
                    {years.map((year) => (
                        <Select.Option key={year} value={year}>
                            {year}
                        </Select.Option>
                    ))}
                </Select>

                <Select
                    placeholder="Month"
                    allowClear
                    style={{ width: 160 }}
                    onChange={(val) => updateFilter("month", val)}
                >
                    {dayjs.months().map((month, index) => (
                        <Select.Option key={index} value={index + 1}>
                            {month}
                        </Select.Option>
                    ))}
                </Select>

                <DatePicker
                    placeholder="From Date"
                    format="DD/MM/YYYY"
                    style={{ width: 160 }}
                    onChange={(date) => updateFilter("fromDate", date)}
                />

                <DatePicker
                    placeholder="To Date"
                    format="DD/MM/YYYY"
                    style={{ width: 160 }}
                    onChange={(date) => updateFilter("toDate", date)}
                />

                <Select
                    placeholder="Status"
                    allowClear
                    style={{ width: 200 }}
                    onChange={(val) => updateFilter("status", val)}
                >
                    {["PENDING", "APPROVED", "REJECTED"].map((status) => (
                        <Select.Option key={status} value={status}>
                            {status}
                        </Select.Option>
                    ))}
                </Select>
            </div>

            <Spin spinning={loading} size="large">
                <Table
                    columns={columns}
                    dataSource={requests.map((item, index) => ({
                        key: index,
                        ...item,
                    }))}
                    pagination={{ pageSize: 20 }}
                    scroll={{ x: true }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "white",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                    bordered
                />
            </Spin>
        </>
    );
}